    import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPersonaChipClass, getCobranzaStatusChip } from '../../../../utils/styles.js';
import { useAuth } from '../../../hooks/useAuth'; 

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// MODALES EXISTENTES EN EL PROYECTO
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';
import ModalDetalleDeuda from '../../../ui/ModalDetalleDeuda.jsx';
import ModalFichaCliente from '../../../ui/ModalFichaCliente.jsx';

// FILTROS Y CONTROLADOR
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import { useCobranzasTable } from './useCobranzaTable';

// --- HELPERS VISUALES DE FECHA Y MONEDA ---
const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        return new Date(dateValue.seconds * 1000).toLocaleDateString('es-AR');
    }
    if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString('es-AR');
    }
    if (typeof dateValue === 'string') {
        if (dateValue.includes('T')) {
            return new Date(dateValue).toLocaleDateString('es-AR');
        }
        const parts = dateValue.split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateValue;
    }
    return '-';
};

const formatNumber = (value) => {
    return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
};

const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    if (dateString.seconds) return new Date(dateString.seconds * 1000).toLocaleDateString('es-AR');
    if (typeof dateString === 'string' && dateString.includes('-')) {
        return dateString.split('-').reverse().join('/');
    }
    return String(dateString);
};

const getImportePriority = (monto, moneda) => {
    const isUSD = String(moneda || '').toUpperCase() === 'USD';
    if (isUSD || monto > 300000) {
        return { label: 'P1', class: 'bg-red-200 text-red-900 border-red-400', val: 1 };
    }
    if (monto >= 100000) {
        return { label: 'P2', class: 'bg-orange-200 text-orange-900 border-orange-400', val: 2 };
    }
    return { label: 'P3', class: 'bg-yellow-100 text-yellow-900 border-yellow-300', val: 3 };
};

// --- FILA MEMOIZADA ---
const TableRow = memo(({ d, isSelected, isLocked, isLockedByMe, columnVisibility, onSelect, onAction, onUnlock }) => {
    const priority = getImportePriority(d.importe, d.moneda);

    return (
        <tr 
            onClick={() => !isLocked && onSelect(d.id)} 
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''} 
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.fechaCarga && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs text-slate-500 tabular-nums">
                    {formatDate(d.ultimaModificacion || d.fechaCarga)}
                </td>
            )}
            
            {columnVisibility.nPoliza && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-sm font-bold text-slate-900">{d.nPoliza}</td>}
            
            {columnVisibility.compania && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-left">
                    <div className="flex items-center justify-left">
                        <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-lg w-fit group-hover:bg-white transition-colors shadow-sm">
                            <div className="text-blue-400"><Icons.Company /></div>
                            <span className="text-[11px] text-blue-800 font-bold uppercase truncate max-w-[160px]">{d.compania}</span>
                        </div>
                    </div>
                </td>
            )}
            
            {columnVisibility.displayNombre && (
                <td className="p-2.5 px-4 border-b border-r border-slate-100 text-sm font-bold text-slate-900 truncate uppercase" title={d.displayNombre}>
                    <div className="flex items-center justify-between gap-2 text-left">
                        <span className="truncate text-left uppercase">{d.displayNombre}</span>
                        {d.tieneDeudaMultiple && (
                            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded-md shrink-0 shadow-sm" title={d.detalleMultiDeuda}>
                                <Icons.MultiDebt />
                                <span className="text-[10px] font-black text-amber-700">+{d.packCount}</span>
                            </div>
                        )}
                    </div>
                </td>
            )}
            
            {columnVisibility.displayDoc && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-sm font-mono text-slate-500">{d.displayDoc}</td>}
            
            {columnVisibility.riesgo && (
                <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                    <div className="flex flex-col text-left">
                        <span className="font-bold text-slate-800 tracking-tight">{d.riesgo}</span>
                        <span className="text-[11px] font-medium text-slate-500 uppercase">{d.subriesgo || 'General'}</span>
                    </div>
                </td>
            )}

            {columnVisibility.importeDeuda && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <div className="flex flex-col items-center justify-center gap-1 text-left">
                        <span className={`px-3 py-1 rounded-lg text-sm font-black border tabular-nums ${priority.class}`}>
                            {d.moneda || '$'} {formatNumber(d.importe)}
                        </span>
                        {d.totalCuotas > 1 && (
                            <div className="flex items-center gap-1 text-[9px] font-black text-red-600 animate-pulse uppercase tracking-tighter">
                                <svg className="w-3 h-3 text-red-600 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L1 21h22L12 2zm1 14h-2v-2h2v2zm0-4h-2V8h2v4z"/></svg>
                                <span>+{d.totalCuotas - 1} CUOTAS PENDIENTES</span>
                            </div>
                        )}
                    </div>
                </td>
            )}

            {columnVisibility.estadoGestion && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs">
                    <span className={`px-3 py-1 rounded-full ${getCobranzaStatusChip(d.estado || 'PENDIENTE')}`}>
                        {d.estado || 'PENDIENTE'}
                    </span>
                </td>
            )}

            {columnVisibility.estadoDeuda && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-slate-800 uppercase">
                    {d.estadoDeuda || '-'}
                </td>
            )}

            {columnVisibility.intentoCobro && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-slate-800">
                    {d.intentoCobro || '0'}
                </td>
            )}

            {columnVisibility.formaPago && (
                <td className="p-2.5 px-3 text-center text-[11px] text-blue-800 font-bold border-b border-r border-slate-100">
                    <span className="bg-blue-50 border border-blue-100 rounded-lg uppercase gap-2 px-3 py-1">{d.formaPago || '-'}</span>
                </td>
            )}

            {columnVisibility.fechaReclamo && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-bold text-red-700 tabular-nums bg-red-50/20">{formatDateShort(d.fechaReclamo)}</td>}

            {columnVisibility.quienInformo && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    {d.quienInformo ? (
                        <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(d.quienInformo)}`}>
                            <span className="text-[11px] font-bold uppercase">{d.quienInformo}</span>
                        </div>
                    ) : '-'}
                </td>
            )}

            {columnVisibility.canal && (
                <td className="p-2.5 px-4 text-sm text-slate-700 border-b border-r border-slate-100">
                    <div className="flex flex-col text-left">
                        <span className="font-bold text-slate-800 tracking-tight">{d.canal}</span>
                        <span className="text-[11px] font-medium text-slate-500 uppercase">{d.canalNombre || '-'}</span>
                    </div>
                </td>
            )}

            {/* Acciones */}
            <td className="p-2.5 px-2 text-center sticky right-0 z-20 border-l border-b border-r border-slate-100 bg-white group-hover:bg-slate-50 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)]">
                <div className="flex justify-center items-center gap-1.5">
                    {isLocked ? (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 animate-pulse shadow-sm">
                            <Icons.Lock /> <span className="text-[9px] font-black uppercase tracking-tighter">{d.bloqueo?.userName || 'Ocupado'}</span>
                        </div>
                    ) : (
                        <>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(d, 'view_debt_deep'); }} className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all active:scale-90" title="Ver Historial de Deuda"><Icons.ChevronRight /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(d, 'view_policy_detail'); }} className="p-1 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all active:scale-90" title="Ver Detalle de Póliza Emisión"><Icons.Policy /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(d, 'view_client_ficha'); }} className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all active:scale-90" title="Ver Ficha Técnica de Cliente"><Icons.User /></button>
                            {isLockedByMe && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); onUnlock(d.path); }} className="p-1 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar Caso">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const CobranzaPolizas = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Consumimos nuestro cerebro estructurado de Cobranzas
    const {
        loading,
        needsHydration,
        clientsCache,
        activeDeuda,
        activePolizaData,
        showDeepDebtModal,
        showPolicyModal,
        showFichaModal,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDeepDebtModal,
        setShowPolicyModal,
        setShowFichaModal,
        setActiveDeuda,
        setActivePolizaData,
        handleAction,
        unlockPoliza
    } = useCobranzasTable();

    // Desestructuramos el estado síncrono del tableState
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        checkIsLocked: checkIsItemLocked
    } = tableState;

    const columnLabels = {
        fechaCarga: "F. Gestión", nPoliza: "N° Póliza", compania: "Cía", displayNombre: "Cliente", displayDoc: "DNI/CUIT", riesgo: "Riesgo/Subr", importeDeuda: "Deuda $", estadoGestion: "Gestión", estadoDeuda: "Estado Deuda", intentoCobro: "Intentos", formaPago: "Pago", fechaReclamo: "Últ. Reclamo", quienInformo: "Informó", canal: "Canal"
    };

    // Liberación automática al desmontar la pestaña
    useEffect(() => {
        const handleUnload = () => { if (activeItemPathRef.current) unlockPoliza(activeItemPathRef.current); };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [unlockPoliza, activeItemPathRef]);

    // Configuración de Filtros Dinámicos
    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        
        const deudasFilteredByCanal = filterValues.canal?.length > 0 ? itemsTable.filter(d => filterValues.canal.includes(d.canal)) : itemsTable;
        const deudasFilteredByNombre = filterValues.canalNombre?.length > 0 ? deudasFilteredByCanal.filter(d => filterValues.canalNombre.includes(d.canalNombre)) : deudasFilteredByCanal;
        const deudasFilteredByRiesgo = filterValues.riesgo?.length > 0 ? itemsTable.filter(d => filterValues.riesgo.includes(d.riesgo)) : itemsTable;

        return [
            { name: 'search', label: 'Buscador', type: 'text', placeholder: 'Cliente o Póliza...', className: 'w-72' },
            { name: 'compania', label: 'Cía', type: 'select', isMulti: true, options: getUnique('compania') },
            { name: 'riesgo', label: 'Riesgo', type: 'select', isMulti: true, options: getUnique('riesgo') },
            { name: 'subriesgo', label: 'Subriesgo', type: 'select', isMulti: true, options: getUnique('subriesgo', deudasFilteredByRiesgo) },
            { name: 'formaPago', label: 'Pago', type: 'select', isMulti: true, options: getUnique('formaPago') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: getUnique('canalNombre', deudasFilteredByCanal) },
            { name: 'subcanalNombre', label: 'Subcanal', type: 'select', isMulti: true, options: getUnique('subcanalNombre', deudasFilteredByNombre) },
            { name: 'quienInformo', label: 'Informó', type: 'select', isMulti: true, options: getUnique('quienInformo') },
            { name: 'estadoGestion', label: 'Est. Gestión', type: 'select', isMulti: true, options: getUnique('estadoGestion') },
            { name: 'estadoDeuda', label: 'Est. Deuda', type: 'select', isMulti: true, options: getUnique('estadoDeuda') },
            { name: 'prioridad', label: 'Prioridad', type: 'select', isMulti: true, options: [{label: 'PRIORIDAD 1 (>300k / USD)', value: 1}, {label: 'PRIORIDAD 2 (100k-300k)', value: 2}, {label: 'PRIORIDAD 3 (<100k)', value: 3}] },
            { name: 'fechaCarga', label: 'F. Gestión', type: 'date-range' },
            { name: 'fechaReclamo', label: 'F. Reclamo', type: 'date-range' },
        ];
    }, [itemsTable, filterValues]);

    // ◄ PREVENCIÓN DE PARPADEO (needsHydration): Demoramos la carga visual hasta sincronizar el 100% de caches relacionales
    if (loading || needsHydration) return (
        <div className="flex h-screen flex-col items-center justify-center bg-white space-y-6">
            <div className="w-16 h-16 border-4 border-red-600/20 border-t-red-600 rounded-full animate-spin"></div>
            <div className="flex flex-col items-center gap-2 text-center text-slate-900">
                <span className="font-black text-[10px] uppercase tracking-[0.3em] text-red-600 animate-pulse text-left">Sincronizando Cobranzas</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-8 text-left">Preparando deudas, pólizas e identidades comerciales...</span>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-screen w-full bg-white text-slate-900 overflow-hidden antialiased font-['Inter',_sans-serif] text-left">
            <div className="h-1 bg-red-600 w-full shrink-0"></div>
            
            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button type="button" onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><Icons.ArrowLeft /></button>
                    <div className="flex items-center gap-4">
                        <div className="p-2 bg-red-50 text-red-600 rounded-lg border border-red-100"><Icons.MultiDebt className="w-6 h-6 text-red-600" /></div>
                        <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Tablero de Cobranzas</h2>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm">
                        <div className="bg-red-600 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Morosos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS CON KEY ESTABLE */}
            <FiltrosDinamicos 
                key="cobranzas-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL RESPONSIVE */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="3200px">
                    <thead>
                        <tr className="bg-[#01032d] text-white">
                            <SortableHeader label="F. Gestión" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="N° Póliza" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Cía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-left" />
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[300px] text-left" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Riesgo / Subr" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[200px] text-left" />
                            <SortableHeader label="Importe Deuda" sortKey="importeDeuda" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Gestión" sortKey="estadoGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Estado Deuda" sortKey="estadoDeuda" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Intentos" sortKey="intentoCobro" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="Pago" sortKey="formaPago" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Últ. Reclamo" sortKey="fechaReclamo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Gestionó" sortKey="quienInformo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Canal" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <th className="w-32 p-4 sticky right-0 top-0 z-[65] bg-slate-900 border-b border-slate-700 uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-left">
                        {paginatedData.map(item => {
                            const isLocked = checkIsItemLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <TableRow 
                                    key={item.id} 
                                    d={item} 
                                    isSelected={selectedRowId === item.id} 
                                    isLocked={isLocked}
                                    isLockedByMe={isLockedByMe}
                                    columnVisibility={columnVisibility} 
                                    onSelect={setSelectedRowId} 
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza}
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN COMÚN (accentClass con tonos rojos corporativos de Cobranzas) */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="morosos"
                    accentClass="text-red-700 bg-red-50 border-red-100"
                />
            </main>

            {/* MODALES RECONECTADOS */}
            {activeDeuda && (
                <>
                    <ModalDetalleDeuda 
                        show={showDeepDebtModal} 
                        onClose={() => { unlockPoliza(activeDeuda.path); setShowDeepDebtModal(false); setActiveDeuda(null); setActivePolizaData(null); }} 
                        poliza={activePolizaData} 
                        client={{ ...clientsCache[activeDeuda.clienteId], nombre: activeDeuda.displayNombre, id: activeDeuda.clienteId }} 
                    />
                    <ModalDetallePoliza 
                        show={showPolicyModal} 
                        onClose={() => { setShowPolicyModal(false); setActivePolizaData(null); }} 
                        poliza={activePolizaData} 
                    />
                    <ModalFichaCliente 
                        show={showFichaModal} 
                        onClose={() => { setShowFichaModal(false); setActiveDeuda(null); }} 
                        client={{ id: activeDeuda.clienteId, ...clientsCache[activeDeuda.clienteId], nombre: activeDeuda.displayNombre }} 
                    />
                </>
            )}
        </div>
    );
};

export default CobranzaPolizas;